import {
  LayoutDashboard,
  Users,
  Users2,
  UserCog,
  UserCheck,
  ShieldCheck,
  Lock,
  Building2,
  Factory,
  MapPin,
  FileBarChart,
  FileText,
  FileSpreadsheet,
  ClipboardList,
  ListChecks,
  ListTodo,
  Workflow,
  Flag,
  FolderKanban,
  FolderTree,
  FolderOpen,
  Tag,
  Package,
  Boxes,
  Box,
  Wrench,
  Cog,
  Settings2,
  HardHat,
  ShoppingCart,
  Truck,
  Ruler,
  Hammer,
  Briefcase,
  Layers,
  Network,
  Image as ImageIcon,
  Sparkles,
  CalendarCheck,
  Plug,
  Wind,
  Sun,
  Zap,
  BarChart3,
  TrendingUp,
  GanttChartSquare,
  type LucideIcon,
} from 'lucide-react';
import type { Feature } from '../../../types/feature';

/**
 * Semantic icon mapping from permission-key module → Lucide icon.
 * Each entry is chosen so the icon reads at a glance ("box-ish thing for
 * assets, blueprint for engineering, etc."). When a module key is missing
 * from this map we fall back to the feature's DB-saved icon (resolveDbIcon)
 * and finally to a generic placeholder.
 */
const MODULE_ICON: Record<string, LucideIcon> = {
  // ── Common / admin
  userManagement: Users,
  roleManagement: ShieldCheck,
  dashboard: LayoutDashboard,
  report: FileBarChart,
  // ── Shared masters
  projectType: Tag,
  assetMaster: Package,
  customer: Building2,
  customerGroup: Users2,
  location: MapPin,
  uomMaster: Ruler,
  documentMaster: FileText,
  manufacturer: Factory,
  // ── OMS
  projectMaster: FolderKanban,
  taskMaster: ListTodo,
  taskExecution: Workflow,
  checkpointMaster: Flag,
  equipmentMaster: Wrench,
  siteSupervisor: HardHat,
  // ── AMS
  assetManagement: Boxes,
  maintenance: Cog,
  // ── EPC
  procurement: ShoppingCart,
  engineering: Hammer,
  epcProject: Wind,
};

/**
 * Map DB icon names (used by the feature form's icon picker) onto Lucide
 * equivalents. Anything we don't recognise will be ignored and the route /
 * key heuristic below takes over.
 */
const DB_ICON_TO_LUCIDE: Record<string, LucideIcon> = {
  GridIcon: LayoutDashboard,
  UserCircleIcon: UserCog,
  UserIcon: UserCheck,
  SettingsIcon: Settings2,
  DatabaseIcon: Layers,
  BoxIcon: Box,
  FolderIcon: FolderOpen,
  TaskIcon: ClipboardList,
  ListIcon: ListChecks,
  TableIcon: FileSpreadsheet,
  PieChartIcon: FileBarChart,
  FileTextIcon: FileText,
  HomeIcon: LayoutDashboard,
  LockIcon: Lock,
  CalenderIcon: CalendarCheck,
  GroupIcon: Users2,
  PlugInIcon: Plug,
  DocsIcon: FileText,
  ShootingStarIcon: Sparkles,
  BoltIcon: Sparkles,
  // EPC / renewable-energy icons
  WindIcon: Wind,
  SolarIcon: Sun,
  EpcIcon: Zap,
  BarChart3Icon: BarChart3,
  TrendingUpIcon: TrendingUp,
  GanttIcon: GanttChartSquare,
  UsersIcon: Users,
  UserGroupIcon: Users2,
};

/**
 * Heuristics on the feature key / route when neither the module map nor the
 * DB icon helps — catches things like "approval", "calendar", "import",
 * "network" that show up across many modules.
 */
const KEYWORD_FALLBACKS: Array<[RegExp, LucideIcon]> = [
  [/dashboard|home/i, LayoutDashboard],
  [/approval|approve/i, ListChecks],
  [/calendar|schedule/i, CalendarCheck],
  [/import|upload/i, FileSpreadsheet],
  [/network|tree|graph/i, Network],
  [/equipment/i, Wrench],
  [/checkpoint|inspection/i, Flag],
  [/customer/i, Building2],
  [/location|site|address/i, MapPin],
  [/manufacture|vendor|supplier/i, Factory],
  [/role/i, ShieldCheck],
  [/user/i, Users],
  [/project/i, FolderKanban],
  [/asset/i, Boxes],
  [/task/i, ListTodo],
  [/document|doc|file/i, FileText],
  [/report/i, FileBarChart],
  [/unit|uom/i, Ruler],
  [/procure|purchase|order/i, ShoppingCart],
  [/engineer|design|cad|drawing/i, Hammer],
  [/maintenance|service/i, Cog],
  [/delivery|ship/i, Truck],
  [/wind|turbine/i, Wind],
  [/solar|photovoltaic|\bpv\b/i, Sun],
  [/epc|renewable|energy/i, Zap],
];

/** Take "projectMaster.read" → "projectMaster" */
function moduleFromPermissionKey(pk: string | undefined): string {
  return (pk ?? '').split('.')[0] ?? '';
}

/** Walks the resolution chain to pick the best icon for a feature. */
export function getFeatureIcon(feature: Pick<Feature, 'icon' | 'permissionKey' | 'key' | 'route'>): LucideIcon {
  // 1) Module-key map (most semantic).
  const moduleKey = moduleFromPermissionKey(feature.permissionKey);
  if (moduleKey && MODULE_ICON[moduleKey]) return MODULE_ICON[moduleKey];

  // 2) Translate the admin's chosen DB icon string (e.g., "FolderIcon") into a Lucide equivalent.
  if (feature.icon && DB_ICON_TO_LUCIDE[feature.icon]) return DB_ICON_TO_LUCIDE[feature.icon];

  // 3) Keyword heuristics on key/route/name.
  const haystack = `${feature.key} ${feature.route}`.toLowerCase();
  for (const [pattern, icon] of KEYWORD_FALLBACKS) {
    if (pattern.test(haystack)) return icon;
  }

  // 4) Default — a neutral, modern stand-in.
  return FolderOpen;
}

/** Re-export for callers that just want the icon component once. */
export type FeatureIconComponent = LucideIcon;
